import { Currency, CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import REWARDS_ABI from 'abis/KodiakRewards.json'
import { ButtonError, ButtonPrimary } from 'components/Button'
import { LightCard } from 'components/Card'
import Column from 'components/Column'
import CurrencyInputPanel from 'components/CurrencyInputPanel'
import CurrencyLogo from 'components/CurrencyLogo'
import FiatValue from 'components/FiatValue'
import Row, { AutoRow, RowBetween } from 'components/Row'
import { Checkbox as BaseCheckbox } from 'components/SearchModal/styleds'
import ConfirmationTransactionStep, {
  ConfirmationTransactionStepProps,
} from 'components/StepLine/common/ConfirmationTransactionStep'
import { useStepLineState, useStepLineStateUpdate } from 'components/StepLine/context/StepLineContext'
import { KODIAK_REWARDS_ADDRESSES } from 'constants/addresses'
import { useConnection } from 'hooks/useConnection'
import { useDividends } from 'hooks/useStaking'
import useTheme from 'hooks/useTheme'
import { useWriteContract } from 'hooks/useTransaction'
import useWaitForTransactionReceipt from 'hooks/useWaitForTransactionReceipt'
import { useTokenBalance } from 'lib/hooks/useCurrencyBalance'
import tryParseCurrencyAmount from 'lib/utils/tryParseCurrencyAmount'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Text } from 'rebass'
import { useTransactionAdder } from 'state/transactions/hooks'
import { TransactionType } from 'state/transactions/types'
import styled from 'styled-components'
import { calculateGasMargin } from 'utils/calculateGasMargin'
import { formatCurrencyAmount } from 'utils/formatCurrencyAmount'
import { formatPercentage } from 'utils/numbers'
import { Abi } from 'viem'

import { TransactionInfoText } from './styleds'

const IncentivesCheckbox = styled(BaseCheckbox).attrs({ type: 'checkbox' })`
  appearance: none;
  width: 16px;
  height: 16px;
  min-width: 16px;
  border-radius: 4px;
  border: 1.5px solid ${({ theme }) => theme.backgroundInteractive};
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  &:checked {
    background: ${({ theme }) => theme.accentAction};
    border-color: ${({ theme }) => theme.accentAction};
  }

  &:checked::after {
    content: '';
    width: 10px;
    height: 6px;
    border-left: 1.5px solid white;
    border-bottom: 1.5px solid white;
    transform: rotate(-45deg) translate(1px, -1px);
  }

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: none;
  }
`

export type AddIncentivesStepState = {
  amount?: CurrencyAmount<Token>
  selectedTokenAddress?: string
  typedValue?: string
  confirmed?: boolean
}

type AddIncentivesViewStepProps = {
  next: VoidFunction
}

export const AddIncentivesViewStep = ({ next }: AddIncentivesViewStepProps) => {
  const theme = useTheme()
  const { account } = useConnection()
  const { rewardsInfo, distributedTokens, distributedTokenAddresses } = useDividends()
  const incentiveTokens = useMemo(
    () => distributedTokens.filter((token): token is Token => !!token),
    [distributedTokens]
  )
  const distributedAddresses = useMemo(
    () => distributedTokenAddresses.filter((address): address is string => !!address),
    [distributedTokenAddresses]
  )

  const stepState = useStepLineState<AddIncentivesStepState>()
  const [selectedTokenAddress, setSelectedTokenAddress] = useState(stepState.selectedTokenAddress)
  const [typedValue, setTypedValue] = useState(stepState.typedValue ?? '')
  const [confirmed, setConfirmed] = useState(stepState.confirmed ?? false)

  const update = useStepLineStateUpdate<AddIncentivesStepState>()

  useEffect(() => {
    if (!selectedTokenAddress && distributedAddresses.length > 0) {
      setSelectedTokenAddress(distributedAddresses[0])
    }
  }, [selectedTokenAddress, distributedAddresses])

  const selectedToken = useMemo(() => {
    if (!selectedTokenAddress) return incentiveTokens[0]
    return (
      incentiveTokens.find((token) => token.address.toLowerCase() === selectedTokenAddress.toLowerCase()) ||
      incentiveTokens[0]
    )
  }, [incentiveTokens, selectedTokenAddress])

  const amount = useMemo(() => {
    return tryParseCurrencyAmount(typedValue, selectedToken || undefined)
  }, [typedValue, selectedToken])

  useEffect(() => {
    update({ amount, selectedTokenAddress, typedValue, confirmed })
  }, [amount, selectedTokenAddress, typedValue, confirmed, update])

  const balance = useTokenBalance(account, selectedToken)
  const isInsufficientBalance = Boolean(amount && balance && amount.greaterThan(balance))
  const isActionDisabled =
    !confirmed || !amount || !amount.greaterThan('0') || !balance || isInsufficientBalance || !selectedToken

  const actionLabel = isInsufficientBalance ? `Insufficient ${selectedToken?.symbol ?? 'balance'}` : 'Add incentives'

  const selectedRewardsInfo = useMemo(() => {
    const targetAddress = selectedTokenAddress ?? selectedToken?.address
    if (!targetAddress) return undefined
    return rewardsInfo.find(
      (info) => info?.pendingAmount?.currency?.address?.toLowerCase() === targetAddress.toLowerCase()
    )
  }, [rewardsInfo, selectedTokenAddress, selectedToken?.address])

  const selectedPendingAmount = selectedRewardsInfo?.pendingAmount
  const selectedTokenSymbol = selectedPendingAmount?.currency?.symbol ?? selectedToken?.symbol

  const cycleRewardsPercent = useMemo(() => {
    if (selectedRewardsInfo?.cycleRewardsPercent === undefined) return null
    const rawPercent = parseFloat(selectedRewardsInfo.cycleRewardsPercent.toString())
    if (Number.isNaN(rawPercent)) return null
    return rawPercent / 100
  }, [selectedRewardsInfo?.cycleRewardsPercent])

  const handleSelectToken = useCallback((currency: Currency) => {
    if (!currency.isToken) return
    setSelectedTokenAddress(currency.address)
  }, [])

  const toggleConfirmed = useCallback(() => {
    setConfirmed((prev) => !prev)
  }, [])

  return (
    <Column gap="20px">
      <LightCard>
        <RowBetween>
          <TransactionInfoText>Remaining incentives</TransactionInfoText>
          <Row style={{ gap: 4, width: 'max-content', alignItems: 'baseline' }}>
            <Text color={theme.textPrimary} fontSize={14}>
              {selectedPendingAmount ? formatCurrencyAmount(selectedPendingAmount, 6) : '-'}
              {selectedTokenSymbol ? ` ${selectedTokenSymbol}` : ''}
            </Text>
            <TransactionInfoText>
              {selectedPendingAmount ? (
                <>
                  (<FiatValue currencyAmount={selectedPendingAmount} />)
                </>
              ) : (
                '(-)'
              )}
            </TransactionInfoText>
          </Row>
        </RowBetween>
        <RowBetween>
          <TransactionInfoText>Current epoch distribution</TransactionInfoText>
          <TransactionInfoText>
            {cycleRewardsPercent === null ? '-' : formatPercentage(cycleRewardsPercent.toString())}
          </TransactionInfoText>
        </RowBetween>
      </LightCard>

      <CurrencyInputPanel
        value={typedValue}
        currency={selectedToken}
        onUserInput={setTypedValue}
        onMax={() => setTypedValue(balance?.toExact() ?? '0')}
        onCurrencySelect={handleSelectToken}
        allowedCurrencies={distributedAddresses.length ? distributedAddresses : undefined}
        preferredCurrencies={incentiveTokens}
        id="add-staking-incentives"
        allowNativeToken={false}
        showMaxButton
        showCategories={false}
        compactSearchModal
      />

      <AutoRow
        as="label"
        style={{
          display: 'flex',
          flexWrap: 'nowrap',
          gap: '8px',
          padding: '0 8px',
        }}
      >
        <IncentivesCheckbox checked={confirmed} onChange={toggleConfirmed} />
        <Text
          style={{
            color: theme.accentWarning,
            userSelect: 'none',
            fontSize: '14px',
          }}
        >
          I understand that these tokens will be used to incentivize xKDK stakers and will not be withdrawable.
        </Text>
      </AutoRow>

      <ButtonPrimary onClick={next} disabled={isActionDisabled}>
        {actionLabel}
      </ButtonPrimary>
    </Column>
  )
}

type AddIncentivesStepProps = {
  next: VoidFunction
  amount?: CurrencyAmount<Token>
  onAddIncentives: () => void
} & Omit<ConfirmationTransactionStepProps, 'children' | 'onConfirm'>

export const useAddIncentivesState = () => {
  const { chainId, account, wagmiPublicClient } = useConnection()
  const rewardsAddress = KODIAK_REWARDS_ADDRESSES[chainId]
  const { amount } = useStepLineState<AddIncentivesStepState>()
  const token = amount?.currency?.isToken ? amount.currency : undefined

  const [attemptingTxn, setAttemptingTxn] = useState(false)
  const [hash, setHash] = useState('')
  const { writeContractAsync } = useWriteContract()
  const addTransaction = useTransactionAdder()
  const waitForTransactionReceipt = useWaitForTransactionReceipt()

  const onAddIncentives = async () => {
    if (!wagmiPublicClient || !account || !rewardsAddress || !amount || !token) return

    setAttemptingTxn(true)
    try {
      const tx = {
        address: rewardsAddress,
        abi: REWARDS_ABI as Abi,
        functionName: 'addRewardsToPending',
        args: [token.address, BigInt(amount.quotient.toString())],
        account,
      }

      const gas = await wagmiPublicClient.estimateContractGas(tx)

      const txHash = await writeContractAsync({ ...tx, gas: calculateGasMargin(gas) })

      setHash(txHash)
      addTransaction(
        {
          hash: txHash,
        },
        {
          type: TransactionType.ADD_STAKING_INCENTIVES,
          amount: `${amount?.toSignificant(6)} ${amount?.currency?.symbol}`,
        }
      )

      await waitForTransactionReceipt({
        hash: txHash,
      })
    } catch (e) {
      console.error('Error on add incentives', e)
    } finally {
      setAttemptingTxn(false)
    }
  }

  const reset = () => {
    setAttemptingTxn(false)
    setHash('')
  }

  return { amount, attemptingTxn, hash, onAddIncentives, reset }
}

const AddIncentivesStep = ({ amount, next, onAddIncentives, ...rest }: AddIncentivesStepProps) => {
  const theme = useTheme()
  const buttonDisabled = !amount || !amount.greaterThan('0')

  return (
    <ConfirmationTransactionStep onConfirm={next} {...rest}>
      <Column gap="6px" width="100%">
        <LightCard>
          <RowBetween>
            <Text color={theme.textPrimary} fontSize={18}>
              {formatCurrencyAmount(amount, 6)}
            </Text>
            <Text>
              <Row>
                <CurrencyLogo currency={amount?.currency} />
                <Text marginLeft="4px" color={theme.textPrimary} fontSize={16}>
                  {amount?.currency.symbol}
                </Text>
              </Row>
            </Text>
          </RowBetween>
        </LightCard>
        <ButtonError onClick={onAddIncentives} disabled={buttonDisabled}>
          Add incentives
        </ButtonError>
      </Column>
    </ConfirmationTransactionStep>
  )
}

export default AddIncentivesStep
